/**
 * HandNameView for Poker Reborn
 *
 * Displays the winning hand name (e.g., "Full House") over the community cards
 * during showdown. Features white text on a dark transparent pill-shaped background.
 *
 * @see docs/screens/room/HAND_NAME.md - Visual specs, positioning, behavior
 */

import { Container, Graphics, Text } from 'pixi.js';
import { addOriginMarker, ORIGIN_COLORS } from '@/utils/OriginDebug';

// =============================================================================
// STYLES
// =============================================================================

/** Hand name display styles */
const HAND_NAME_STYLES = {
  // Text
  fill: 0xffffff, // White
  fontSize: 24,
  fontFamily: 'Roboto, Arial, sans-serif',
  fontWeight: 'bold' as const,

  // Background
  backgroundColor: 0x000000, // Black
  backgroundAlpha: 0.7, // 70% opacity
  paddingX: 20, // Horizontal padding
  paddingY: 8, // Vertical padding
  cornerRadius: 16, // Pill shape

  // Position (over lower part of community cards)
  yOffset: 40,
};

// =============================================================================
// COMPONENT
// =============================================================================

export class HandNameView extends Container {
  private background: Graphics;
  private nameText: Text;

  constructor() {
    super();

    // Create background (behind text)
    this.background = new Graphics();
    this.background.visible = false;
    this.addChild(this.background);

    // Create text
    this.nameText = new Text({
      text: '',
      style: {
        fill: HAND_NAME_STYLES.fill,
        fontSize: HAND_NAME_STYLES.fontSize,
        fontFamily: HAND_NAME_STYLES.fontFamily,
        fontWeight: HAND_NAME_STYLES.fontWeight,
      },
    });
    this.nameText.anchor.set(0.5);
    this.nameText.y = HAND_NAME_STYLES.yOffset;
    this.nameText.visible = false;
    this.addChild(this.nameText);

    // Debug: Add origin marker if ?showOrigins=true
    addOriginMarker(this, ORIGIN_COLORS.handName);
  }

  // ===========================================================================
  // PUBLIC API
  // ===========================================================================

  /**
   * Show hand name over the community cards
   * @param name - The hand name to display (e.g., "Full House")
   */
  show(name: string): void {
    console.log('[HandNameView] show:', name);
    this.nameText.text = name;
    this.nameText.visible = true;

    // Draw background sized to text
    this.drawBackground();
    this.background.visible = true;
  }

  /**
   * Hide the hand name text
   */
  hide(): void {
    console.log('[HandNameView] hide');
    this.nameText.visible = false;
    this.nameText.text = '';
    this.background.visible = false;
  }

  /**
   * Get the text element for potential animation effects
   */
  getText(): Text {
    return this.nameText;
  }

  /**
   * Get the background element for potential animation effects
   */
  getBackground(): Graphics {
    return this.background;
  }

  // ===========================================================================
  // PRIVATE METHODS
  // ===========================================================================

  /**
   * Draw the pill-shaped background sized to fit the text
   */
  private drawBackground(): void {
    const { paddingX, paddingY, cornerRadius, backgroundColor, backgroundAlpha, yOffset } =
      HAND_NAME_STYLES;

    // Get text bounds
    const textWidth = this.nameText.width;
    const textHeight = this.nameText.height;

    // Calculate background dimensions
    const bgWidth = textWidth + paddingX * 2;
    const bgHeight = textHeight + paddingY * 2;

    // Draw pill shape centered on text position
    this.background.clear();
    this.background.roundRect(
      -bgWidth / 2,
      yOffset - bgHeight / 2,
      bgWidth,
      bgHeight,
      cornerRadius
    );
    this.background.fill({ color: backgroundColor, alpha: backgroundAlpha });
  }
}
